import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Physician Notification subsection adapter
 * Handles physician notification thresholds with nested less/greater pairs (14 fields)
 * This handles vital sign thresholds that trigger physician notifications
 */
export class PhysicianNotificationAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Temperature thresholds
    const tempLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS
    )?.at(0)

    const tempGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER
    )?.at(0)

    // Pulse thresholds
    const pulseLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS
    )?.at(0)

    const pulseGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER
    )?.at(0)

    // Respirations thresholds
    const respirationsLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS
    )?.at(0)

    const respirationsGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER
    )?.at(0)

    // Blood pressure thresholds
    const bpLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS
    )?.at(0)

    const bpGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER
    )?.at(0)

    // Weight thresholds
    const weightLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS
    )?.at(0)

    const weightGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER
    )?.at(0)

    // EtCO2 saturation thresholds
    const etco2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS
    )?.at(0)

    const etco2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER
    )?.at(0)

    // O2 saturation thresholds
    const o2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS
    )?.at(0)

    const o2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER
    )?.at(0)

    return {
      [DischargeFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS]: tempLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER]: tempGreater,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS]: pulseLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER]: pulseGreater,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS]: respirationsLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER]: respirationsGreater,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_BP_LESS]: bpLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_BP_GREATER]: bpGreater,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS]: weightLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER]: weightGreater,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS]: etco2SaturationLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER]: etco2SaturationGreater,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS]: o2SaturationLess,
      [DischargeFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER]: o2SaturationGreater
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_BP_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_BP_LESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_BP_GREATER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_BP_GREATER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS,
        value: this.getFormDataField(
          DischargeFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS
        )
      })
    }
    if (
      this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER)
    ) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER,
        value: this.getFormDataField(
          DischargeFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS,
        value: this.getFormDataField(DischargeFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER,
        value: this.getFormDataField(
          DischargeFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER
        )
      })
    }

    return mutations
  }
}
